import React, { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import type { ToastItem } from './ToastContext';

interface ToastContainerProps {
  toasts: ToastItem[];
  onDismiss: (id: string) => void;
}

const darkTypeConfig = {
  success: {
    container:
      'bg-neutral-900/95 backdrop-blur-md border border-neutral-700/80 border-l-4 border-l-emerald-500 shadow-xl shadow-black/30',
    barTrack: 'bg-neutral-800',
    barFill: 'bg-emerald-500',
    text: 'text-neutral-100',
    closeHover: 'hover:bg-neutral-800/80',
  },
  error: {
    container:
      'bg-neutral-900/95 backdrop-blur-md border border-neutral-700/80 border-l-4 border-l-red-500 shadow-xl shadow-black/30',
    barTrack: 'bg-neutral-800',
    barFill: 'bg-red-500',
    text: 'text-neutral-100',
    closeHover: 'hover:bg-neutral-800/80',
  },
  info: {
    container:
      'bg-neutral-900/95 backdrop-blur-md border border-neutral-700/80 border-l-4 border-l-indigo-500 shadow-xl shadow-black/30',
    barTrack: 'bg-neutral-800',
    barFill: 'bg-indigo-500',
    text: 'text-neutral-100',
    closeHover: 'hover:bg-neutral-800/80',
  },
};

const lightTypeConfig = {
  success: {
    container:
      'bg-white/95 backdrop-blur-md border border-neutral-200 border-l-4 border-l-emerald-500 shadow-lg shadow-neutral-300/30',
    barTrack: 'bg-neutral-200',
    barFill: 'bg-emerald-500',
    text: 'text-neutral-900',
    closeHover: 'hover:bg-neutral-100',
  },
  error: {
    container:
      'bg-white/95 backdrop-blur-md border border-neutral-200 border-l-4 border-l-red-500 shadow-lg shadow-neutral-300/30',
    barTrack: 'bg-neutral-200',
    barFill: 'bg-red-500',
    text: 'text-neutral-900',
    closeHover: 'hover:bg-neutral-100',
  },
  info: {
    container:
      'bg-white/95 backdrop-blur-md border border-neutral-200 border-l-4 border-l-indigo-500 shadow-lg shadow-neutral-300/30',
    barTrack: 'bg-neutral-200',
    barFill: 'bg-indigo-500',
    text: 'text-neutral-900',
    closeHover: 'hover:bg-neutral-100',
  },
};

interface ToastItemProps {
  toast: ToastItem;
  onDismiss: () => void;
  isWhiteTheme: boolean;
}

const ToastItemComponent: React.FC<ToastItemProps> = ({ toast, onDismiss, isWhiteTheme }) => {
  const durationMs = toast.duration ?? 5000;
  const typeConfig = isWhiteTheme ? lightTypeConfig : darkTypeConfig;
  const config = typeConfig[toast.type ?? 'info'];

  return (
    <div
      className={`toast-item pointer-events-auto rounded-xl overflow-hidden ${config.container}`}
      role="alert"
    >
      <div className="flex items-start gap-3 px-4 py-3">
        <p className={`text-sm font-medium flex-1 leading-snug ${config.text}`}>{toast.message}</p>
        <button
          type="button"
          onClick={onDismiss}
          className={`p-1.5 rounded-lg transition-colors shrink-0 ${config.closeHover} ${config.text} opacity-70 hover:opacity-100`}
          aria-label="Dismiss"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
      <div className={`h-0.5 ${config.barTrack} overflow-hidden`}>
        <div
          className={`h-full ${config.barFill} rounded-r-full origin-left`}
          style={{
            animation: `toast-shrink ${durationMs}ms linear forwards`,
          }}
          onAnimationEnd={onDismiss}
        />
      </div>
    </div>
  );
};

const ToastContainer: React.FC<ToastContainerProps> = ({ toasts, onDismiss }) => {
  const getIsWhiteTheme = () =>
    typeof document !== 'undefined' &&
    document.documentElement.getAttribute('data-theme') === 'white';
  const [isWhiteTheme, setIsWhiteTheme] = useState<boolean>(getIsWhiteTheme);

  useEffect(() => {
    setIsWhiteTheme(getIsWhiteTheme());
    if (typeof document === 'undefined' || typeof MutationObserver === 'undefined') return;

    const observer = new MutationObserver(() => {
      setIsWhiteTheme(getIsWhiteTheme());
    });

    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-theme'],
    });

    return () => observer.disconnect();
  }, []);

  if (toasts.length === 0) return null;

  return (
    <div
      className="fixed bottom-6 right-6 z-[9999] flex flex-col gap-3 max-w-sm w-full pointer-events-none"
      aria-live="polite"
    >
      {toasts.map((toast) => (
        <ToastItemComponent
          key={toast.id}
          toast={toast}
          onDismiss={() => onDismiss(toast.id)}
          isWhiteTheme={isWhiteTheme}
        />
      ))}
    </div>
  );
};

export default ToastContainer;
